<script setup>
import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';

import { INBOX_TYPES } from 'dashboard/helper/inbox';
import SidebarActionsHeader from 'dashboard/components-next/SidebarActionsHeader.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import TiktokShopOrders from './TiktokShop/Orders.vue';
import TiktokShopProducts from './TiktokShop/Products.vue';
import { useUISettings } from 'dashboard/composables/useUISettings';

const store = useStore();
const { t } = useI18n();
const { uiSettings, updateUISettings } = useUISettings();

const currentChat = computed(() => store.getters.getSelectedChat);
const activeTabIndex = ref(0);
const activeTabValue = ref('ORDER');

const availableTabs = ref([
  {
    value: 'ORDER',
    label: t('CONVERSATION.TIKTOKSHOP.TABS.ORDER'),
    count: 0,
  },
  {
    value: 'PRODUCT',
    label: t('CONVERSATION.TIKTOKSHOP.TABS.PRODUCT'),
    count: 0,
  },
]);

const handleTabChange = selectedTab => {
  activeTabValue.value = selectedTab.value;
  activeTabIndex.value = availableTabs.value.findIndex(
    tab => tab.value === selectedTab.value
  );
};

const closeTiktokShopPanel = () => {
  updateUISettings({
    is_shared_files_panel_open: false,
    is_copilot_panel_open: false,
    is_contact_sidebar_open: false,
    is_shopee_panel_open: false,
    is_lazada_panel_open: false,
    is_tiktok_shop_panel_open: false,
  });
};

const channelType = computed(() => {
  return currentChat.value?.channel || currentChat.value?.meta?.channel || '';
});

const shouldShowTiktokShopPanel = computed(() => {
  const isTiktokShopPanelOpen = uiSettings.value?.is_tiktok_shop_panel_open;
  return isTiktokShopPanelOpen && channelType.value === INBOX_TYPES.TIKTOK_SHOP;
});
</script>

<template>
  <div
    v-if="shouldShowTiktokShopPanel"
    class="ltr:border-l rtl:border-r border-n-weak h-full overflow-hidden z-10 w-[320px] min-w-[320px] 2xl:min-w-[360px] 2xl:w-[360px] flex flex-col bg-n-background"
  >
    <div class="flex flex-col h-full">
      <SidebarActionsHeader
        :title="$t('CONVERSATION.SIDEBAR.TIKTOKSHOP')"
        @close="closeTiktokShopPanel"
      />
      <div id="tts-tabs" class="flex-1">
        <TabBar
          class="bg-white"
          :tabs="availableTabs"
          :initial-active-tab="activeTabIndex"
          @tab-changed="handleTabChange"
        />
      </div>
      <div class="flex-auto overflow-y-auto h-full w-full">
        <TiktokShopOrders
          v-if="activeTabValue === 'ORDER'"
          :current-chat="currentChat"
        />
        <TiktokShopProducts
          v-if="activeTabValue === 'PRODUCT'"
          :current-chat="currentChat"
        />
      </div>
    </div>
  </div>
  <template v-else />
</template>

<style lang="scss">
#tts-tabs {
  @apply flex-none border-b border-n-weak bg-white dark:bg-slate-900;

  .tabs {
    @apply bg-white dark:bg-slate-900 p-0;
  }

  .divider {
    @apply hidden;
  }

  button {
    @apply border-none border-0 outline-none rounded-none;
    @apply dark:bg-slate-900;

    &.active {
      @apply border-woot-600 border-b border-solid;
    }
  }
}
</style>
